/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import Logo from '../../../assets/images/logo-black.png';
import AuthDecoration from '../../../assets/images/auth-decoration.png';
import AuthService from '../../../services/AuthService';

const errorClasses =
  'bg-red-100 dark:bg-red-400/30 text-red-600 dark:text-red-400 px-3 py-2 rounded';
const successClasses =
  'bg-green-100 dark:bg-green-400/30 text-green-600 dark:text-green-400 px-3 py-2 rounded';

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
});

export default function ForgotPasswordPage() {
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        await AuthService.sendResetPasswordLink(values.email);

        setErrorMessage(null);

        setSuccessMessage(
          'If found, a password reset email was sent to that email address'
        );
      } catch (error) {
        console.log(error);
        const message = error.response?.data?.message;
        setSuccessMessage(null);
        setErrorMessage(message ?? 'Something went wrong');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <main className="bg-white dark:bg-slate-900">
      <div className="relative md:flex">
        {/* Content */}
        <div className="md:w-1/2">
          <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
            {/* Header */}
            <div className="flex-1">
              <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                {/* Logo */}
                <Link className="block" to="/login">
                  <img
                    src={Logo}
                    alt="Close sidebar"
                    className="w-[42px] h-auto"
                  />
                </Link>
              </div>
            </div>

            <div className="max-w-sm mx-auto w-full px-4 py-8">
              <h1 className="text-3xl text-slate-800 dark:text-slate-100 font-bold mb-6">
                Reset your Password ✨
              </h1>
              {/* Form */}
              <form onSubmit={formik.handleSubmit} noValidate>
                <div className="space-y-4">
                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      htmlFor="email"
                    >
                      Email Address
                    </label>
                    <input
                      id="email"
                      className="form-input w-full"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.email && formik.errors.email ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.email}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-6">
                  <button
                    disabled={isSubmitting}
                    type="submit"
                    className="btn bg-indigo-500 hover:bg-indigo-600 text-white ml-auto"
                  >
                    Send Reset Link
                  </button>
                </div>
              </form>
              {/* Footer */}
              <div className="pt-5 mt-6 relative">
                {/* Error message */}
                {(errorMessage || successMessage) && (
                  <div className="absolute w-full top-0">
                    <div
                      className={errorMessage ? errorClasses : successClasses}
                    >
                      <span className="text-sm">
                        {errorMessage || successMessage}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Image */}
        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 md:w-1/2"
          aria-hidden="true"
        >
          <img
            className="object-cover object-center w-full h-full"
            src={`https://images.unsplash.com/photo-1572120360610-d971b9d7767c?q=80&h=${window.innerHeight}&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`}
            width="760"
            height="1024"
            alt="Authentication"
          />
          <img
            className="absolute top-1/4 left-0 -translate-x-1/2 ml-8 hidden lg:block"
            src={AuthDecoration}
            width="218"
            height="224"
            alt="Authentication decoration"
          />
        </div>
      </div>
    </main>
  );
}
